import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ConnectionChangeDto,
  ConnectionChangeRequestTypeDisplayValues,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import { combineLatestWith, filter, map, shareReplay } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { AllAreaEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { articles } from '@bo-client/shared/constants/articles';
import { path } from '@bo-client/shared/constants/path';
import { OrderRequestWizardService } from '@bo-client/nap/order-request-wizard/order-request-wizard.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { ConnectionChangeRequestTypeConfig } from '@bo-client/shared/config.model';

@Component({
  selector: 'app-order-request-wizard-proxy',
  templateUrl: './order-request-wizard-proxy.component.html',
  styleUrl: './order-request-wizard-proxy.component.scss'
})
export class OrderRequestWizardProxyComponent implements OnInit {
  model$: Observable<ConnectionChangeDto | null> = of(null);
  isActive$: Observable<boolean> = of(false);

  private destroyRef = inject(DestroyRef);
  private orderRequestWizardService = inject(OrderRequestWizardService);
  private route = inject(ActivatedRoute);
  private dataShareService = inject(DataShareService);
  private toastrService = inject(ToastrService);
  private router = inject(Router);

  configLoaded$ = of(false);

  ngOnInit() {
    this.configLoaded$ = this.dataShareService.tenantConfig$.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((config) => !!config && !!config.connectionChangeRequest),
      map(() => {
        return true;
      }),
      shareReplay(1)
    );
    this.isActive$ = this.route.paramMap.pipe(
      takeUntilDestroyed(this.destroyRef),
      combineLatestWith(this.dataShareService.tenantConfig$),
      filter(([, config]) => !!config),
      map(([params, config]) => {
        const types: ConnectionChangeRequestTypeConfig[] =
          config?.connectionChangeRequest?.connectionChangeRequestTypes;
        if (!types?.length || !params.has('type') || !params.has('area')) {
          return false;
        }
        const area = params.get('area');
        const type = params.get('type');
        return !!ConnectionChangeRequestTypeConfig.getConnectionChangeRequestTypeConfig(
          area as AllAreaEnum,
          type as ConnectionChangeRequestTypeEnum,
          config?.connectionChangeRequest?.connectionChangeRequestTypes
        );
      }),
      shareReplay(1)
    );
    this.model$ = this.route.paramMap.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((params) => {
        if (params.has('type') && params.has('area')) {
          const selectedType = Object.values(ConnectionChangeRequestTypeEnum).find(
            (type) => type.toUpperCase() === params.get('type').toUpperCase()
          );
          const selectedArea = Object.values(AllAreaEnum).find(
            (area) => area.toUpperCase() === params.get('area').toUpperCase()
          );
          if (!selectedType || !selectedArea) {
            return null;
          }
          const model = new ConnectionChangeDto(selectedType);
          model.area = selectedArea;
          return model;
        }
        return null;
      }),
      combineLatestWith(this.dataShareService.currentTenant$),
      map(([model, tenant]) => {
        model.tenant = tenant;
        return model;
      }),
      filter((model) => !!model?.area && !!model?.connectionChangeRequestType),
      shareReplay(1)
    );
  }

  submitNewRequest(dto: ConnectionChangeDto) {
    if (!dto) {
      this.toastrService.error('Fehler beim Übermitteln der Daten.');
      return;
    }
    this.orderRequestWizardService.submitNewRequest(dto).subscribe({
      next: () => {
        const subjectType = ConnectionChangeRequestTypeDisplayValues[dto.connectionChangeRequestType];
        const subject = `${articles.upper.formal[subjectType?.gender].acc} ${
          dto.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.TEMPORARY
            ? 'temporären Anschluss'
            : subjectType?.name
        }`;

        this.toastrService.success(
          'Vielen Dank, wir haben Ihre Anfrage erhalten.',
          'Antrag erfolgreich gesendet'
        );
        const message = `Wir haben Ihren Antrag für ${subject} erhalten. Sie werden in Kürze eine Bestätigungsmail mit allen Informationen zu Ihrer Anfrage erhalten.`;
        this.router
          .navigate([path.external + '/' + path.goToThanksPage + '/' + message], {
            replaceUrl: true
          })
          .then();
      },
      error: (err) => {
        this.toastrService.error(
          'Beim Übermitteln ist ein Fehler aufgetreten: ' + err.status + ' ' + err.error
        );
      }
    });
  }

  returnToEntry() {
    this.router.navigate([`${path.external}/${path.orderRequestWizard}`]).then();
  }
}
